import React, { useState, useEffect } from 'react';
import { 
  Building, 
  Clock, 
  Bell, 
  Users,
  Mail,
  Phone,
  Package,
  Plus,
  Trash
} from 'lucide-react';
import  Card from '@/components/ui/Card';
import  Button  from '@/components/ui/Button';
import  Input  from '@/components/ui/Input';
import ApiService from '../../../services/api';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await ApiService.getBusinessSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await ApiService.updateBusinessSettings(settings);
      // Show success message
    } catch (error) {
      console.error('Error saving settings:', error);
      // Show error message
    }
  };

  const addServiceField = () => {
    setSettings(prev => ({
      ...prev,
      services: [
        ...prev.services,
        { name: '', duration: 30, price: 0 }
      ]
    }));
  };

  const removeServiceField = (index) => {
    setSettings(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Settings</h2>
        <Button onClick={handleSave}>Save Changes</Button>
      </div>

      {/* Settings Navigation */}
      <div className="flex space-x-2 border-b">
        {[
          { id: 'profile', label: 'Business Profile', icon: Building },
          { id: 'hours', label: 'Working Hours', icon: Clock },
          { id: 'services', label: 'Services', icon: Package },
          { id: 'team', label: 'Team', icon: Users },
          { id: 'notifications', label: 'Notifications', icon: Bell }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent hover:border-gray-300'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Business Profile Settings */}
      {activeTab === 'profile' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1">Business Name</label>
              <Input
                value={settings?.businessName || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, businessName: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={settings?.email || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone</label>
                <Input
                  type="tel"
                  value={settings?.phone || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <Input
                value={settings?.address || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Working Hours Settings */}
      {activeTab === 'hours' && (
        <Card className="p-6">
          <div className="space-y-4">
            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
              <div key={day} className="flex items-center space-x-4">
                <div className="w-32">{day}</div>
                <select 
                  className="rounded-lg border-gray-300"
                  value={settings?.workingHours?.[index]?.start || '09:00'}
                >
                  {/* Add time options */}
                </select>
                <span>to</span>
                <select 
                  className="rounded-lg border-gray-300"
                  value={settings?.workingHours?.[index]?.end || '17:00'}
                >
                  {/* Add time options */}
                </select>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={settings?.workingHours?.[index]?.closed}
                  />
                  <span>Closed</span>
                </label>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Services Settings */}
      {activeTab === 'services' && (
        <Card className="p-6">
          <div className="space-y-4">
            {settings?.services.map((service, index) => (
              <div key={index} className="flex items-center space-x-4">
                <Input
                  placeholder="Service name"
                  value={service.name}
                  onChange={(e) => {
                    const newServices = [...settings.services];
                    newServices[index].name = e.target.value;
                    setSettings(prev => ({ ...prev, services: newServices }));
                  }}
                  className="flex-1"
                />
                <Input
                  type="number"
                  placeholder="Duration (min)"
                  value={service.duration}
                  onChange={(e) => {
                    const newServices = [...settings.services];
                    newServices[index].duration = e.target.value;
                    setSettings(prev => ({ ...prev, services: newServices }));
                  }}
                  className="w-32"
                />
                <Input
                  type="number"
                  placeholder="Price"
                  value={service.price}
                  onChange={(e) => {
                    const newServices = [...settings.services];
                    newServices[index].price = e.target.value;
                    setSettings(prev => ({ ...prev, services: newServices }));
                  }}
                  className="w-32"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeServiceField(index)}
                >
                  <Trash className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button onClick={addServiceField} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        </Card>
      )}

      {/* Team Settings */}
      {activeTab === 'team' && (
        <Card className="p-6">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Team Members</h3>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Member
              </Button>
            </div>

            <div className="space-y-4">
              {settings?.team?.map((member, index) => (
                <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {member.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-medium">{member.name}</h4>
                      <p className="text-sm text-gray-500">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">Edit</Button>
                    <Button variant="ghost" size="sm">
                      <Trash className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Notification Settings */}
      {activeTab === 'notifications' && (
        <Card className="p-6">
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">Email Notifications</h3>
            <div className="space-y-4">
              {[
                { id: 'newBooking', label: 'New Booking Notifications' },
                { id: 'bookingReminder', label: 'Booking Reminders' },
                { id: 'bookingCancellation', label: 'Booking Cancellations' },
                { id: 'customerMessages', label: 'Customer Messages' },
                { id: 'dailySummary', label: 'Daily Summary' }
              ].map((notification) => (
                <label key={notification.id} className="flex items-center justify-between">
                  <span>{notification.label}</span>
                  <input
                    type="checkbox"
                    checked={settings?.notifications?.[notification.id]}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          [notification.id]: e.target.checked
                        }
                      }));
                    }}
                    className="form-checkbox h-5 w-5 text-primary-600"
                  />
                </label>
              ))}
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Notification Email</h3>
              <Input
                type="email"
                value={settings?.notificationEmail || ''}
                onChange={(e) => setSettings(prev => ({
                  ...prev,
                  notificationEmail: e.target.value
                }))}
                placeholder="notifications@yourbusiness.com"
              />
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSave}>Save Changes</Button>
      </div>
    </div>
  );
};

export default Settings;